import { useCallback, useEffect, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField, Button, FieldShell, Modal, PlusCircleSolidIcon, StatusBadge, Tile, ToggleField, type Column, type StatusTone } from "@fawkes/ui";

import {
  CreditCard,
  Ban,
  Pencil,
  Pause,
  Play,
  Download,
  FileText,
  List,
  Send,
  Heart,
  User,
  Newspaper,
  MailOpen,
  Mail,
  RefreshCw,
  Tag,
  Euro, CalendarDays, CheckCircle2 } from "lucide-react";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Relaties Donateur-tab migration: the customer-counterpart to animalTiles, for the role-gated Donateur
// tab. Reads GET /api/relaties/{id}/donateur(/donaties) via useCustom (relative url — the dataProvider
// prepends /api). Write phase (W5a): the "Nieuwe donatie" + "Periodieke donatie" (create/edit) dialogs
// and the pauze/stop row actions, all POST/PUT to the ROLE_RELATIE_EDIT-gated endpoints; a mutation
// rebuilds + refetches the tab in one round-trip. Status / frequency / payment-method / donation-type
// arrive as raw codes; the i18n maps below render the Dutch labels (mirroring Jmix DonateurLabels).

interface DonateurTabData {
  donateurschap: { status: string; actief: boolean; donateurSinds: string | null };
  stats: {
    totaalGedoneerd: number;
    totaalDitJaar: number;
    laatsteDonatie: string | null;
    aantal: number;
    laatsteBedankmail: string | null;
  };
  communicatie: {
    lastContactAt: string | null;
    laatsteBedankmail: string | null;
    newsletterEnabled: boolean | null;
    preferredChannel: string | null;
    internalNote: string | null;
  };
  fiscaal: {
    jaarHuidig: number;
    totaalHuidig: number;
    verwachtRest: number;
    jaarVorig: number;
    totaalVorig: number;
    laatsteOverzicht: string | null;
    referentie: string | null;
    documenten: { id: number; fileName: string | null; year: number | null; reference: string | null; downloadUrl: string | null }[];
  };
  periodiek: {
    id: number; amount: number | null; frequency: string | null; paymentMethod: string | null;
    nextCollectionDate: string | null; startedAt: string | null;
    status: string | null; mandaatOntbreekt: boolean;
  }[];
  issues: { severity: string; code: string; message: string; actionLabel: string | null; targetId: number | null }[];
}

interface DonatieRegel {
  id: number;
  datum: string | null;
  type: string | null;
  status: string | null;
  bedrag: number | null;
  gepland: boolean;
}

const STATUS_KEY: Record<string, string> = {
  ACTIEF: "relatieDetailView.donateur.status.ACTIEF",
  GEPAUZEERD: "relatieDetailView.donateur.status.GEPAUZEERD",
  GESTOPT: "relatieDetailView.donateur.status.GESTOPT",
  INACTIEF: "relatieDetailView.donateur.status.INACTIEF",
};
const STATUS_TONE: Record<string, StatusTone> = {
  ACTIEF: "success",
  GEPAUZEERD: "warning",
  GESTOPT: "neutral",
  INACTIEF: "neutral",
};
const FREQ_KEY: Record<string, string> = {
  MAANDELIJKS: "relatieDetailView.donateur.freq.MAANDELIJKS",
  PER_KWARTAAL: "relatieDetailView.donateur.freq.PER_KWARTAAL",
  JAARLIJKS: "relatieDetailView.donateur.freq.JAARLIJKS",
};
const PAYMENT_KEY: Record<string, string> = {
  AUTOMATISCHE_INCASSO: "relatieDetailView.donateur.payment.AUTOMATISCHE_INCASSO",
  BANKOVERSCHRIJVING: "relatieDetailView.donateur.payment.BANKOVERSCHRIJVING",
  CONTANT: "relatieDetailView.donateur.payment.CONTANT",
  PIN: "relatieDetailView.donateur.payment.PIN",
  IDEAL: "relatieDetailView.donateur.payment.IDEAL",
};
const DONATIE_TYPE_KEY: Record<string, string> = {
  EENMALIG: "relatieDetailView.donateur.type.EENMALIG",
  PERIODIEK: "relatieDetailView.donateur.type.PERIODIEK",
};
const DONATIE_STATUS_KEY: Record<string, string> = {
  BETAALD: "relatieDetailView.donateur.dstatus.BETAALD",
  OPENSTAAND: "relatieDetailView.donateur.dstatus.OPENSTAAND",
  GEPLAND: "relatieDetailView.donateur.dstatus.GEPLAND",
};
const FREQ_OPT_KEY: Record<string, string> = {
  MAANDELIJKS: "relatieDetailView.donateur.freqOpt.MAANDELIJKS",
  PER_KWARTAAL: "relatieDetailView.donateur.freqOpt.PER_KWARTAAL",
  JAARLIJKS: "relatieDetailView.donateur.freqOpt.JAARLIJKS",
};

// Een lege datum is in de bron "Geen", niet een streepje. formatDate geeft zelf al "—" terug,
// dus de terugval moet op de ruwe waarde staan en niet op wat er uit de opmaak komt.
function datum(waarde: string | null | undefined, locale: string, t: (k: string) => string): string {
  return waarde ? formatDate(waarde, locale) : t("relatieDetailView.donateur.geenWaarde");
}

function label(map: Record<string, string>, code: string | null, t: (k: string) => string): string {
  if (!code) return "—";
  const key = map[code];
  return key ? t(key) : code;
}

/** Parse a euro amount accepting comma or dot; returns null when empty/unparseable. */
function parseAmount(raw: string): number | null {
  const v = Number(raw.trim().replace(",", "."));
  return raw.trim() === "" || !Number.isFinite(v) ? null : v;
}

type SaveNotice = "saved" | "conflict" | "forbidden" | "error" | null;

/** Map a mutation error's status code to a tile notice (403/409/4xx). */
function noticeFromError(err: unknown): SaveNotice {
  const code = (err as { statusCode?: number }).statusCode;
  return code === 409 ? "conflict" : code === 403 ? "forbidden" : "error";
}

function noticeText(notice: SaveNotice, t: (k: string) => string): string | null {
  switch (notice) {
    case "saved":
      return t("relatieDetailView.donateur.saved");
    case "conflict":
      return t("relatieDetailView.donateur.saveConflict");
    case "forbidden":
      return t("relatieDetailView.donateur.saveForbidden");
    case "error":
      return t("relatieDetailView.donateur.saveError");
    default:
      return null;
  }
}

function useDonateur<T>(relatieId: string, sub: string) {
  const { data, isLoading, isError, refetch } = useCustom({ url: `relaties/${relatieId}/${sub}`, method: "get" });
  return { data: data?.data as T | undefined, isLoading, isError, refetch };
}

function Loading({ isLoading, isError, t }: { isLoading: boolean; isError: boolean; t: (k: string) => string }) {
  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError) return <p className="fawkes-error">{t("relatieDetailView.tileLoadFailed")}</p>;
  return null;
}

// ---- Issue banner ----------------------------------------------------------------------------------
function IssueBanner({ issues, onAction }: { issues: DonateurTabData["issues"]; onAction: (targetId: number) => void }) {
  if (!issues || issues.length === 0) return null;
  return (
    <div className="fawkes-donateur-issues">
      {issues.map((issue, i) => (
        <div key={`${issue.code}-${i}`} className="fawkes-donateur-issue" role="alert">
          <span className="fawkes-donateur-issue__icon" aria-hidden="true">⚠</span>
          <span className="fawkes-donateur-issue__msg">{issue.message}</span>
          {issue.actionLabel && issue.targetId != null && (
            <button
              type="button"
              className="fawkes-donateur-issue__action"
              onClick={() => onAction(issue.targetId as number)}
            >
              {issue.actionLabel}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function DonateurTab({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useDonateur<DonateurTabData>(relatieId, "donateur");
  // A single reload path: refetch the tab (stats/periodiek/issues) and bump a key the donation-overview
  // table watches so its planned/historical rows refresh too.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => {
    refetch();
    setReloadKey((k) => k + 1);
  }, [refetch]);
  // The "Mandaat aanvullen" banner action opens the recurring edit dialog hosted in the periodiek tile.
  const [bannerEditId, setBannerEditId] = useState<number | null>(null);

  if (isLoading || isError) return <Loading isLoading={isLoading} isError={isError} t={t} />;
  const d = data;
  if (!d) return <p className="fawkes-tile-placeholder">{t("relatieDetailView.donateur.geenWaarde")}</p>;

  return (
    <div>
      <IssueBanner issues={d.issues} onAction={setBannerEditId} />
      <div className="fawkes-relatie-detail__tab-tiles">
        <DonateurschapTile relatieId={relatieId} donateurschap={d.donateurschap} periodiek={d.periodiek} reload={reload} />

        <DonatieoverzichtTile relatieId={relatieId} stats={d.stats} reloadKey={reloadKey} reload={reload} />

        <PeriodiekeDonatieTile
          relatieId={relatieId}
          periodiek={d.periodiek}
          reload={reload}
          externalEditId={bannerEditId}
          onExternalEditConsumed={() => setBannerEditId(null)}
        />

        <FiscaleGegevensTile fiscaal={d.fiscaal} />

        <CommunicatieBedankTile relatieId={relatieId} communicatie={d.communicatie} stats={d.stats} reload={reload} />
      </div>
    </div>
  );
}

// ---- Donateurschap tile (toggle: activate / stop / stop-all) ---------------------------------------
function DonateurschapTile({
  relatieId,
  donateurschap,
  periodiek,
  reload,
}: {
  relatieId: string;
  donateurschap: DonateurTabData["donateurschap"];
  periodiek: DonateurTabData["periodiek"];
  reload: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: saving } = useCustomMutation();
  const [notice, setNotice] = useState<SaveNotice>(null);
  const [confirmStop, setConfirmStop] = useState(false);

  const post = (sub: string) => {
    setNotice(null);
    mutate(
      { url: `relaties/${relatieId}/donateur/profile/${sub}`, method: "post", values: {} },
      { onSuccess: () => { setNotice("saved"); reload(); }, onError: (err) => setNotice(noticeFromError(err)) },
    );
  };

  const onToggle = (checked: boolean) => {
    if (checked) {
      post("activate");
    } else if (periodiek && periodiek.length > 0) {
      // stopping with active recurrings → confirm (they will be ended too), mirroring the Jmix dialog
      setConfirmStop(true);
    } else {
      post("stop");
    }
  };

  const nt = noticeText(notice, t);

  const kop = (
    <span className="fawkes-relatie-tiletitel fawkes-donateur-tegelkop">
      <Heart size={18} aria-hidden="true" fill="currentColor" />
      {t("relatieDetailView.donateur.donateurschapTitle")}
    </span>
  );
  return (
    <Tile title={kop}>
      <div className="fawkes-relatie-tile">
        <div className="fawkes-donateur-regels">
          <div className="fawkes-donateur-regel">
            <CheckCircle2 size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.actiefToggle")}</span>
            <span className="fawkes-donateur-regel__schuif">
              <ToggleField
                label=""
                checked={donateurschap.actief}
                disabled={saving}
                onChange={(e) => onToggle(e.target.checked)}
              />
              <StatusBadge tone={STATUS_TONE[donateurschap.status] ?? "neutral"}>
                {label(STATUS_KEY, donateurschap.status, t)}
              </StatusBadge>
            </span>
          </div>
          <div className="fawkes-donateur-regel">
            <CalendarDays size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.donateurSinds")}</span>
            <span className="fawkes-donateur-regel__waarde">{datum(donateurschap.donateurSinds, locale, t)}</span>
          </div>
        </div>

        {/* Jmix-parity helper note: the status is derived from donations; the toggle is the manual-stop override. */}
        <p className="fawkes-donateur-hint">
          <span className="fawkes-donateur-hint__icon" aria-hidden="true">ⓘ</span>
          {t("relatieDetailView.donateur.statusHint")}
        </p>

        {nt && <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>}

        <Modal
          open={confirmStop}
          title={t("relatieDetailView.donateur.dialog.stopProfileTitle")}
          onClose={() => setConfirmStop(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmStop(false)}>{t("common.cancel")}</Button>
              <Button variant="primary" disabled={saving} onClick={() => { setConfirmStop(false); post("stop-all"); }}>
                {t("relatieDetailView.donateur.stoppen")}
              </Button>
            </>
          }
        >
          <p>{t("relatieDetailView.donateur.dialog.stopProfileIntro")}</p>
          <ul className="fawkes-donateur-stop-list">
            {periodiek.map((r) => (
              <li key={r.id}>
                {formatEuro(r.amount ?? 0, locale)} {label(FREQ_KEY, r.frequency, t)} · {label(PAYMENT_KEY, r.paymentMethod, t)}
              </li>
            ))}
          </ul>
          <p className="fawkes-donateur-stop-confirm">{t("relatieDetailView.donateur.dialog.stopProfileConfirm")}</p>
        </Modal>
      </div>
    </Tile>
  );
}

const PERIODE_OPTIONS = [3, 6, 12, 0];

function DonatieoverzichtTile({
  relatieId,
  stats,
  reloadKey,
  reload,
}: {
  relatieId: string;
  stats: DonateurTabData["stats"];
  reloadKey: number;
  reload: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [maanden, setMaanden] = useState(12);
  const { data, isLoading, isError, refetch } = useDonateur<DonatieRegel[]>(relatieId, `donateur/donaties?maanden=${maanden}`);
  const rows = Array.isArray(data) ? data : [];
  // "Bekijk alle donaties" negeert het venster van de keuzelijst — daarvoor is het de knop.
  const { data: alleData } = useDonateur<DonatieRegel[]>(relatieId, "donateur/donaties?maanden=0");
  const alleRijen = Array.isArray(alleData) ? alleData : rows;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [alleOpen, setAlleOpen] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  // Refetch the donation table whenever a sibling mutation bumps the shared reload key.
  useEffect(() => {
    if (reloadKey > 0) refetch();
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitDonation = (values: Record<string, unknown>) => {
    setNotice(null);
    mutate(
      { url: `relaties/${relatieId}/donateur/donaties`, method: "post", values },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setNotice("saved");
          reload();
        },
        onError: (err) => setNotice(noticeFromError(err)),
      },
    );
  };

  const columns: Column<DonatieRegel>[] = [
    { header: t("relatieDetailView.donateur.col.datum"), cell: (r) => formatDate(r.datum, locale) },
    { header: t("relatieDetailView.donateur.col.type"), cell: (r) => label(DONATIE_TYPE_KEY, r.type, t) },
    {
      header: t("relatieDetailView.donateur.col.status"),
      cell: (r) => (
        <StatusBadge tone={r.gepland ? "neutral" : r.status === "BETAALD" ? "success" : "warning"}>
          {label(DONATIE_STATUS_KEY, r.status, t)}
        </StatusBadge>
      ),
    },
    { header: t("relatieDetailView.donateur.col.bedrag"), cell: (r) => formatEuro(r.bedrag ?? 0, locale) },
  ];

  const nt = noticeText(notice, t);
  const aantalGepland = rows.filter((r) => r.gepland).length;
  const buitenVenster = Math.max(0, stats.aantal - (rows.length - aantalGepland));

  const kop = (
    <span className="fawkes-relatie-tiletitel fawkes-donateur-tegelkop">
      <Euro size={18} aria-hidden="true" />
      {t("relatieDetailView.donateur.overzichtTitle")}
      <button
        type="button"
        className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
        onClick={() => { setNotice(null); setDialogOpen(true); }}
      >
        <PlusCircleSolidIcon width={16} height={16} /> {t("relatieDetailView.donateur.nieuweDonatie")}
      </button>
    </span>
  );
  return (
    <Tile title={kop}>
      <div className="fawkes-relatie-tile">
        <div className="fawkes-relatie-facturen__totals fawkes-donateur-stats">
          <div className="fawkes-relatie-facturen__total">
            <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.donateur.totaalGedoneerd")}</span>
            <span className="fawkes-relatie-facturen__total-value">{formatEuro(stats.totaalGedoneerd, locale)}</span>
          </div>
          <div className="fawkes-relatie-facturen__total">
            <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.donateur.ditJaar")}</span>
            <span className="fawkes-relatie-facturen__total-value">{formatEuro(stats.totaalDitJaar, locale)}</span>
          </div>
          <div className="fawkes-relatie-facturen__total">
            <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.donateur.laatsteDonatie")}</span>
            <span className="fawkes-relatie-facturen__total-value">{datum(stats.laatsteDonatie, locale, t)}</span>
          </div>
          <div className="fawkes-relatie-facturen__total">
            <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.donateur.aantal")}</span>
            <span className="fawkes-relatie-facturen__total-value">{stats.aantal}</span>
          </div>
        </div>

        <div className="fawkes-donateur-toolbar">
          <FieldShell label={t("relatieDetailView.donateur.periode.label")} className="fawkes-donateur-periodeveld">
            <select
              className="fui-field__input fui-select"
              value={String(maanden)}
              onChange={(e) => setMaanden(Number(e.target.value))}
            >
              {PERIODE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m === 0
                    ? t("relatieDetailView.donateur.periode.alle")
                    : t("relatieDetailView.donateur.periode.maanden", { args: [m, m] })}
                </option>
              ))}
            </select>
          </FieldShell>
        </div>

        {nt && (
          <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>
        )}

        {isLoading || isError ? (
          <Loading isLoading={isLoading} isError={isError} t={t} />
        ) : rows.length === 0 ? (
          <p className="fawkes-tile-placeholder">{t("relatieDetailView.donateur.geenDonaties")}</p>
        ) : (
          <>
            {/* De geplande incasso's krijgen in de bron een eigen tint: het zijn regels die nog moeten
                gebeuren en die je niet moet verwarren met wat al binnen is. */}
            <div className="fawkes-donateur-lijst">
              <IndeelbareTabel tabelKey="donateur-tiles-geen-donaties"
                columns={columns}
                rows={rows}
                getRowKey={(r) => `${r.gepland ? "p" : "d"}-${r.id}`}
                rowClassName={(r) => (r.gepland ? "fawkes-donateur-rij--gepland" : undefined)}
                empty={t("relatieDetailView.donateur.geenDonaties")}
              />
            </div>
            <div className="fawkes-relatie-tegelvoet fawkes-donateur-lijstvoet">
              <button type="button" className="fawkes-relatie-alleslink" onClick={() => setAlleOpen(true)}>
                <List size={15} aria-hidden="true" /> {t("relatieDetailView.donateur.bekijkAlleDonaties")}
              </button>
              <span className="fawkes-relatie-tegelvoet__rest">
                {t("relatieDetailView.nogMeer", { args: [buitenVenster, stats.aantal] })}
                {aantalGepland > 0 && ` \u00b7 ${t("relatieDetailView.donateur.aantalGepland", { args: [aantalGepland] })}`}
              </span>
            </div>
            <Modal
              open={alleOpen}
              title={t("relatieDetailView.donateur.alleDonaties")}
              size="wide"
              onClose={() => setAlleOpen(false)}
              footer={<Button variant="secondary" onClick={() => setAlleOpen(false)}>{t("common.close")}</Button>}
            >
              <div className="fawkes-relatie-alles">
                <IndeelbareTabel tabelKey="donateur-tiles-geen-donaties-2"
                  columns={columns}
                  rows={alleRijen}
                  getRowKey={(r) => `alle-${r.gepland ? "p" : "d"}-${r.id}`}
                  rowClassName={(r) => (r.gepland ? "fawkes-donateur-rij--gepland" : undefined)}
                  empty={t("relatieDetailView.donateur.geenDonaties")}
                />
              </div>
            </Modal>
          </>
        )}

        {dialogOpen && (
          <NieuweDonatieDialog saving={saving} onClose={() => setDialogOpen(false)} onSubmit={submitDonation} />
        )}
      </div>
    </Tile>
  );
}

function PeriodiekeDonatieTile({
  relatieId,
  periodiek,
  reload,
  externalEditId,
  onExternalEditConsumed,
}: {
  relatieId: string;
  periodiek: DonateurTabData["periodiek"];
  reload: () => void;
  externalEditId: number | null;
  onExternalEditConsumed: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: saving } = useCustomMutation();
  const [notice, setNotice] = useState<SaveNotice>(null);
  // dialog: null = closed; { id: null } = create; { id } = edit that recurring
  const [dialog, setDialog] = useState<{ id: number | null } | null>(null);
  const [confirmStop, setConfirmStop] = useState<number | null>(null);

  // The "Mandaat aanvullen" banner action (parent) opens the edit dialog for the flagged recurring.
  useEffect(() => {
    if (externalEditId != null) {
      setNotice(null);
      setDialog({ id: externalEditId });
      onExternalEditConsumed();
    }
  }, [externalEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = (url: string, method: "post" | "put", values: Record<string, unknown>, onOk?: () => void) => {
    setNotice(null);
    mutate(
      { url: `relaties/${relatieId}/donateur/${url}`, method, values },
      {
        onSuccess: () => {
          onOk?.();
          setNotice("saved");
          reload();
        },
        onError: (err) => setNotice(noticeFromError(err)),
      },
    );
  };

  const submitRecurring = (id: number | null, values: Record<string, unknown>) => {
    if (id == null) {
      run("recurring", "post", values, () => setDialog(null));
    } else {
      run(`recurring/${id}`, "put", values, () => setDialog(null));
    }
  };

  const nt = noticeText(notice, t);

  const kop = (
    <span className="fawkes-relatie-tiletitel fawkes-donateur-tegelkop">
      <RefreshCw size={18} aria-hidden="true" />
      {t("relatieDetailView.donateur.periodiekTitle")}
      <button
        type="button"
        className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
        onClick={() => { setNotice(null); setDialog({ id: null }); }}
      >
        <PlusCircleSolidIcon width={16} height={16} /> {t("relatieDetailView.donateur.periodiekTitle")}
      </button>
    </span>
  );
  return (
    <Tile title={kop}>
      <div className="fawkes-relatie-tile">
        {!periodiek || periodiek.length === 0 ? (
          <div className="fawkes-donateur-leeg">
            <RefreshCw size={26} aria-hidden="true" className="fawkes-donateur-leeg__icoon" />
            <span className="fawkes-donateur-leeg__tekst">{t("relatieDetailView.donateur.geenPeriodiek")}</span>
            <Button variant="primary" onClick={() => { setNotice(null); setDialog({ id: null }); }}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("relatieDetailView.donateur.periodiekToevoegen")}
            </Button>
          </div>
        ) : (
          periodiek.map((r) => (
            <div key={r.id} className="fawkes-donateur-recurring">
              <div className="fawkes-donateur-recurring__head">
                <div className="fawkes-donateur-recurring__amount">
                  {formatEuro(r.amount ?? 0, locale)} <span className="fawkes-donateur-recurring__freq">{label(FREQ_KEY, r.frequency, t)}</span>
                  {/* The Jmix tile marks each agreement with its status, and flags an automatic
                      collection that cannot actually be collected. */}
                  <StatusBadge tone={STATUS_TONE[r.status ?? ""] ?? "neutral"}>
                    {label(STATUS_KEY, r.status, t)}
                  </StatusBadge>
                  {r.mandaatOntbreekt && (
                    <StatusBadge tone="warning">{t("relatieDetailView.donateur.geenMandaat")}</StatusBadge>
                  )}
                </div>
                <div className="fawkes-donateur-recurring__actions">
                  {r.status === "GEPAUZEERD" ? (
                    <button
                      type="button"
                      className="fawkes-donateur-kaartknop"
                      title={t("relatieDetailView.donateur.hervatten")}
                      aria-label={t("relatieDetailView.donateur.hervatten")}
                      onClick={() => run(`recurring/${r.id}/resume`, "post", {})}
                    >
                      <Play size={15} aria-hidden="true" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="fawkes-donateur-kaartknop"
                      title={t("relatieDetailView.donateur.pauzeren")}
                      aria-label={t("relatieDetailView.donateur.pauzeren")}
                      onClick={() => run(`recurring/${r.id}/pause`, "post", {})}
                    >
                      <Pause size={15} aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="fawkes-donateur-kaartknop"
                    title={t("relatieDetailView.donateur.bewerken")}
                    aria-label={t("relatieDetailView.donateur.bewerken")}
                    onClick={() => { setNotice(null); setDialog({ id: r.id }); }}
                  >
                    <Pencil size={15} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="fawkes-donateur-kaartknop fawkes-donateur-kaartknop--stop"
                    title={t("relatieDetailView.donateur.stoppen")}
                    aria-label={t("relatieDetailView.donateur.stoppen")}
                    onClick={() => { setNotice(null); setConfirmStop(r.id); }}
                  >
                    <Ban size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="fawkes-donateur-recurring__meta">
                <CreditCard size={15} aria-hidden="true" className="fawkes-donateur-recurring__meta-icoon" />
                <span>{label(PAYMENT_KEY, r.paymentMethod, t)}</span>
              </div>
              <div className="fawkes-donateur-recurring__meta">
                <CalendarDays size={15} aria-hidden="true" className="fawkes-donateur-recurring__meta-icoon" />
                <span>
                  {t("relatieDetailView.donateur.inningEnStart", {
                    args: [datum(r.nextCollectionDate, locale, t), datum(r.startedAt, locale, t)],
                  })}
                </span>
              </div>
            </div>
          ))
        )}


        {nt && (
          <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>
        )}

        {dialog && (
          <PeriodiekeDonatieDialog
            relatieId={relatieId}
            editId={dialog.id}
            saving={saving}
            onClose={() => setDialog(null)}
            onSubmit={(values) => submitRecurring(dialog.id, values)}
          />
        )}

        <Modal
          open={confirmStop != null}
          title={t("relatieDetailView.donateur.dialog.stopTitle")}
          onClose={() => setConfirmStop(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirmStop(null)}>{t("common.cancel")}</Button>
              <Button
                variant="primary"
                disabled={saving}
                onClick={() => { const id = confirmStop; if (id != null) run(`recurring/${id}/stop`, "post", {}, () => setConfirmStop(null)); }}
              >
                {t("relatieDetailView.donateur.stoppen")}
              </Button>
            </>
          }
        >
          <p>{t("relatieDetailView.donateur.dialog.stopConfirm")}</p>
        </Modal>
        <p className="fawkes-donateur-hint">
          <span className="fawkes-donateur-hint__icon" aria-hidden="true">ⓘ</span>
          {t("relatieDetailView.donateur.periodiekHint")}
        </p>
      </div>
    </Tile>
  );
}

// ---- Nieuwe donatie dialog -------------------------------------------------------------------------
const BETAALWIJZE_DONATIE = ["CONTANT", "PIN", "BANKOVERSCHRIJVING", "AUTOMATISCHE_INCASSO"];
const DONATIE_STATUS_OPTS = ["BETAALD", "OPENSTAAND"];
const DONATIE_TYPE_OPTS = ["EENMALIG", "PERIODIEK"];
const FREQ_OPTS = ["MAANDELIJKS", "PER_KWARTAAL", "JAARLIJKS"];

function NieuweDonatieDialog({
  saving,
  onClose,
  onSubmit,
}: {
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
}) {
  const t = useTranslate();
  const [datum, setDatum] = useState(todayIso());
  const [bedrag, setBedrag] = useState("");
  const [type, setType] = useState("EENMALIG");
  const [frequentie, setFrequentie] = useState("MAANDELIJKS");
  const [betaalwijze, setBetaalwijze] = useState("BANKOVERSCHRIJVING");
  const [status, setStatus] = useState("BETAALD");
  const [iban, setIban] = useState("");
  const [omschrijving, setOmschrijving] = useState("");
  const [bedankmail, setBedankmail] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    const amount = parseAmount(bedrag);
    if (!datum || amount == null) {
      setErr(t("relatieDetailView.donateur.err.datumBedrag"));
      return;
    }
    onSubmit({
      donationDate: datum,
      amount,
      donationType: type,
      status,
      paymentMethod: betaalwijze,
      description: omschrijving.trim() || null,
      sendThankYou: bedankmail,
      iban: iban.trim() || null,
    });
  };

  return (
    <Modal
      open
      title={t("relatieDetailView.donateur.dialog.donatieTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-donateur-form">
        <div className="fawkes-donateur-form__row2">
          <FieldShell label={t("relatieDetailView.donateur.form.datum")} required>
            <DateField className="fui-field__input" max={todayIso()} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </FieldShell>
          <FieldShell label={t("relatieDetailView.donateur.form.bedrag")} required>
            <input className="fui-field__input" inputMode="decimal" placeholder="0,00" value={bedrag} onChange={(e) => setBedrag(e.target.value)} />
          </FieldShell>
          <FieldShell label={t("relatieDetailView.donateur.form.type")}>
            <SelectInput value={type} onChange={setType} options={DONATIE_TYPE_OPTS} map={DONATIE_TYPE_KEY} t={t} />
          </FieldShell>
          {type === "PERIODIEK" && (
            <FieldShell label={t("relatieDetailView.donateur.form.frequentie")}>
              <SelectInput value={frequentie} onChange={setFrequentie} options={FREQ_OPTS} map={FREQ_OPT_KEY} t={t} />
            </FieldShell>
          )}
          <FieldShell label={t("relatieDetailView.donateur.form.betaalwijze")}>
            <SelectInput value={betaalwijze} onChange={setBetaalwijze} options={BETAALWIJZE_DONATIE} map={PAYMENT_KEY} t={t} />
          </FieldShell>
          <FieldShell label={t("relatieDetailView.donateur.form.status")}>
            <SelectInput value={status} onChange={setStatus} options={DONATIE_STATUS_OPTS} map={DONATIE_STATUS_KEY} t={t} />
          </FieldShell>
        </div>
        <FieldShell label={t("relatieDetailView.donateur.form.iban")}>
          <input className="fui-field__input" maxLength={34} placeholder="NL00 BANK 0000 0000 00" value={iban} onChange={(e) => setIban(e.target.value)} />
        </FieldShell>
        <FieldShell label={t("relatieDetailView.donateur.form.omschrijving")}>
          <textarea className="fui-field__input fawkes-donateur-form__area" maxLength={500} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
        </FieldShell>
        <label className="fawkes-donateur-form__check">
          <input type="checkbox" checked={bedankmail} onChange={(e) => setBedankmail(e.target.checked)} />
          <span>{t("relatieDetailView.donateur.form.bedankmail")}</span>
        </label>
        {err && <p className="fawkes-error">{err}</p>}
      </div>
    </Modal>
  );
}

// ---- Periodieke donatie dialog (create + edit) -----------------------------------------------------
const BETAALWIJZE_PERIODIEK = ["AUTOMATISCHE_INCASSO", "BANKOVERSCHRIJVING"];

interface RecurringEdit {
  id: number;
  amount: number | null;
  frequency: string | null;
  startedAt: string | null;
  paymentMethod: string | null;
  mandatePresent: boolean | null;
  remark: string | null;
  iban: string | null;
  version: number | null;
}

function PeriodiekeDonatieDialog({
  relatieId,
  editId,
  saving,
  onClose,
  onSubmit,
}: {
  relatieId: string;
  editId: number | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
}) {
  const t = useTranslate();
  const isEdit = editId != null;
  // Editor-only fetch of the full edit form (incl. iban/mandate/remark, which the public tab omits).
  const { data: editData, isLoading: loadingEdit } = useCustom({
    url: `relaties/${relatieId}/donateur/recurring/${editId}`,
    method: "get",
    queryOptions: { enabled: isEdit },
  });
  const existing = isEdit ? (editData?.data as RecurringEdit | undefined) : undefined;

  const [bedrag, setBedrag] = useState("");
  const [frequentie, setFrequentie] = useState("MAANDELIJKS");
  const [startdatum, setStartdatum] = useState(todayIso());
  const [betaalwijze, setBetaalwijze] = useState("AUTOMATISCHE_INCASSO");
  const [mandaat, setMandaat] = useState(true);
  const [iban, setIban] = useState("");
  const [opmerking, setOpmerking] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  // Seed the form from the fetched recurring once (edit mode).
  useEffect(() => {
    if (!isEdit || seeded || !existing) return;
    setBedrag(existing.amount != null ? String(existing.amount) : "");
    setFrequentie(existing.frequency ?? "MAANDELIJKS");
    setStartdatum(existing.startedAt ?? todayIso());
    setBetaalwijze(existing.paymentMethod ?? "AUTOMATISCHE_INCASSO");
    setMandaat(existing.mandatePresent ?? true);
    setIban(existing.iban ?? "");
    setOpmerking(existing.remark ?? "");
    setSeeded(true);
  }, [isEdit, seeded, existing]);

  const submit = () => {
    const amount = parseAmount(bedrag);
    if (amount == null || !startdatum) {
      setErr(t("relatieDetailView.donateur.err.bedragStart"));
      return;
    }
    if (betaalwijze === "AUTOMATISCHE_INCASSO") {
      if (!iban.trim()) {
        setErr(t("relatieDetailView.donateur.err.ibanRequired"));
        return;
      }
      if (!mandaat) {
        setErr(t("relatieDetailView.donateur.err.mandaatRequired"));
        return;
      }
    }
    onSubmit({
      amount,
      frequency: frequentie,
      startedAt: startdatum,
      paymentMethod: betaalwijze,
      mandatePresent: mandaat,
      remark: opmerking.trim() || null,
      iban: iban.trim() || null,
      version: existing?.version ?? null,
    });
  };

  const incasso = betaalwijze === "AUTOMATISCHE_INCASSO";

  return (
    <Modal
      open
      title={t(isEdit ? "relatieDetailView.donateur.dialog.periodiekEditTitle" : "relatieDetailView.donateur.dialog.periodiekTitle")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={saving || (isEdit && loadingEdit)} onClick={submit}>{t("common.save")}</Button>
        </>
      }
    >
      {isEdit && loadingEdit ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : (
        <div className="fawkes-donateur-form">
          <div className="fawkes-donateur-form__row2">
            <FieldShell label={t("relatieDetailView.donateur.form.bedrag")} required>
              <input className="fui-field__input" inputMode="decimal" placeholder="0,00" value={bedrag} onChange={(e) => setBedrag(e.target.value)} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.donateur.form.frequentie")}>
              <SelectInput value={frequentie} onChange={setFrequentie} options={FREQ_OPTS} map={FREQ_OPT_KEY} t={t} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.donateur.form.startdatum")} required>
              <DateField className="fui-field__input" max={todayIso()} value={startdatum} onChange={(e) => setStartdatum(e.target.value)} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.donateur.form.betaalwijze")}>
              <SelectInput value={betaalwijze} onChange={setBetaalwijze} options={BETAALWIJZE_PERIODIEK} map={PAYMENT_KEY} t={t} />
            </FieldShell>
          </div>
          <label className="fawkes-donateur-form__check">
            <input type="checkbox" checked={mandaat} onChange={(e) => setMandaat(e.target.checked)} />
            <span>{t("relatieDetailView.donateur.form.mandaat")}</span>
          </label>
          <FieldShell label={t("relatieDetailView.donateur.form.iban")} required={incasso}>
            <input className="fui-field__input" maxLength={34} placeholder="NL00 BANK 0000 0000 00" value={iban} onChange={(e) => setIban(e.target.value)} />
          </FieldShell>
          <FieldShell label={t("relatieDetailView.donateur.form.opmerking")}>
            <textarea className="fui-field__input fawkes-donateur-form__area" maxLength={500} value={opmerking} onChange={(e) => setOpmerking(e.target.value)} />
          </FieldShell>
          {err && <p className="fawkes-error">{err}</p>}
        </div>
      )}
    </Modal>
  );
}

// ---- small form helpers ----------------------------------------------------------------------------
function SelectInput({
  value,
  onChange,
  options,
  map,
  t,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  map: Record<string, string>;
  t: (k: string) => string;
}) {
  return (
    <select className="fui-field__input fui-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o} value={o}>{label(map, o, t)}</option>
      ))}
    </select>
  );
}

function FiscaleGegevensTile({ fiscaal }: { fiscaal: DonateurTabData["fiscaal"] }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const kop = (
    <span className="fawkes-relatie-tiletitel fawkes-donateur-tegelkop">
      <FileText size={18} aria-hidden="true" />
      {t("relatieDetailView.donateur.fiscaalTitle")}
      <button
        type="button"
        className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
        disabled
        title={t("relatieDetailView.donateur.actieLater")}
      >
        <FileText size={15} aria-hidden="true" /> {t("relatieDetailView.donateur.jaaroverzicht")}
      </button>
    </span>
  );
  return (
    <Tile title={kop}>
      <div className="fawkes-relatie-tile">
        <div className="fawkes-donateur-regels">
          <div className="fawkes-donateur-regel">
            <CheckCircle2 size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.jaaroverzichtGewenst")}</span>
            <span className={waardeKlasse(fiscaal.documenten.length > 0 ? "ja" : null)}>
              {fiscaal.documenten.length > 0
                ? t("common.yes")
                : t("relatieDetailView.donateur.onbekend")}
            </span>
          </div>
          <div className="fawkes-donateur-regel">
            <Euro size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">
              {t("relatieDetailView.donateur.totaalJaar", { args: [fiscaal.jaarHuidig] })}
            </span>
            <span className="fawkes-donateur-regel__waarde fawkes-donateur-bedrag">
              {formatEuro(fiscaal.totaalHuidig, locale)}
              {fiscaal.verwachtRest > 0 && (
                <span className="fawkes-donateur-verwacht">
                  {t("relatieDetailView.donateur.verwachtRestKort", {
                    args: [formatEuro(fiscaal.verwachtRest, locale)],
                  })}
                </span>
              )}
            </span>
          </div>
          <div className="fawkes-donateur-regel">
            <Euro size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">
              {t("relatieDetailView.donateur.totaalJaar", { args: [fiscaal.jaarVorig] })}
            </span>
            <span className="fawkes-donateur-regel__waarde">{formatEuro(fiscaal.totaalVorig, locale)}</span>
          </div>
          <div className="fawkes-donateur-regel">
            <CalendarDays size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.laatsteOverzicht")}</span>
            <span className={waardeKlasse(fiscaal.laatsteOverzicht)}>
              {datum(fiscaal.laatsteOverzicht, locale, t)}
            </span>
          </div>
          <div className="fawkes-donateur-regel">
            <Tag size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
            <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.referentie")}</span>
            <span className={`${waardeKlasse(fiscaal.referentie)} fawkes-donateur-referentie`}>
              {fiscaal.referentie || t("relatieDetailView.donateur.geenWaarde")}
            </span>
          </div>
        </div>
        {fiscaal.documenten.length > 0 && (
          <div className="fawkes-donateur-docs">
            {fiscaal.documenten.map((doc) => (
              <div key={doc.id} className="fawkes-donateur-doc">
                <FileText size={17} aria-hidden="true" className="fawkes-donateur-doc__icoon" />
                <span className="fawkes-donateur-doc__naam">
                  {doc.fileName ?? `#${doc.id}`}
                  {doc.year ? <span className="fawkes-donateur-doc__jaar"> · {doc.year}</span> : null}
                </span>
                {doc.downloadUrl ? (
                  <a
                    className="fawkes-donateur-doc__actie"
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download size={14} aria-hidden="true" /> {t("relatieDetailView.donateur.bekijken")}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
        <p className="fawkes-donateur-hint">
          <span className="fawkes-donateur-hint__icon" aria-hidden="true">ⓘ</span>
          {t("relatieDetailView.donateur.fiscaalHint")}
        </p>
      </div>
    </Tile>
  );
}

// Een waarde die er niet is leest anders dan een waarde die er wel is: de bron zet er een
// gedempt-cursieve opmaak op (donateur-tile__info-value--empty).
function waardeKlasse(waarde: string | null | undefined): string {
  return waarde
    ? "fawkes-donateur-regel__waarde"
    : "fawkes-donateur-regel__waarde fawkes-donateur-regel__waarde--leeg";
}

function CommunicatieBedankTile({
  relatieId,
  communicatie,
  stats,
  reload,
}: {
  relatieId: string;
  communicatie: DonateurTabData["communicatie"];
  stats: DonateurTabData["stats"];
  reload: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: saving } = useCustomMutation();
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const [voorkeur, setVoorkeur] = useState(communicatie.preferredChannel ?? "");
  const [nieuwsbrief, setNieuwsbrief] = useState(Boolean(communicatie.newsletterEnabled));
  const [notitie, setNotitie] = useState(communicatie.internalNote ?? "");

  // Re-seed when the tab reloads (after a save) or the relation changes.
  useEffect(() => {
    setVoorkeur(communicatie.preferredChannel ?? "");
    setNieuwsbrief(Boolean(communicatie.newsletterEnabled));
    setNotitie(communicatie.internalNote ?? "");
    setEditing(false);
    setNotice(null);
  }, [relatieId, communicatie.preferredChannel, communicatie.newsletterEnabled, communicatie.internalNote]);

  const save = () => {
    setNotice(null);
    mutate(
      {
        url: `relaties/${relatieId}/donateur/communicatie`,
        method: "put",
        values: { preferredChannel: voorkeur.trim() || null, newsletterEnabled: nieuwsbrief, internalNote: notitie.trim() || null },
      },
      {
        onSuccess: () => { setEditing(false); setNotice("saved"); reload(); },
        onError: (err) => setNotice(noticeFromError(err)),
      },
    );
  };

  const nt = noticeText(notice, t);

  const kop = (
    <span className="fawkes-relatie-tiletitel fawkes-donateur-tegelkop">
      <Mail size={18} aria-hidden="true" />
      {t("relatieDetailView.donateur.communicatieTitle")}
      <button
        type="button"
        className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
        disabled
        title={t("relatieDetailView.donateur.actieLater")}
      >
        <PlusCircleSolidIcon width={16} height={16} /> {t("relatieDetailView.donateur.contactmoment")}
      </button>
    </span>
  );
  if (editing) {
    return (
      <Tile title={kop}>
        <div className="fawkes-relatie-tile">
          <div className="fawkes-donateur-form">
            <label className="fawkes-donateur-form__check">
              <input type="checkbox" checked={nieuwsbrief} onChange={(e) => setNieuwsbrief(e.target.checked)} />
              <span>{t("relatieDetailView.donateur.nieuwsbrief.label")}</span>
            </label>
            <FieldShell label={t("relatieDetailView.donateur.voorkeur")}>
              <input className="fui-field__input" maxLength={20} value={voorkeur} onChange={(e) => setVoorkeur(e.target.value)} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.donateur.interneNotitie")}>
              <textarea className="fui-field__input fawkes-donateur-form__area" maxLength={2000} value={notitie} onChange={(e) => setNotitie(e.target.value)} />
            </FieldShell>
          </div>
          <div className="fawkes-donateur-tile-actions">
            <Button variant="secondary" onClick={() => { setEditing(false); setNotice(null); }} disabled={saving}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={save} disabled={saving}>{t("common.save")}</Button>
          </div>
          {nt && <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>}
        </div>
      </Tile>
    );
  }

  const newsletter =
    communicatie.newsletterEnabled == null
      ? t("relatieDetailView.donateur.nieuwsbrief.onbekend")
      : communicatie.newsletterEnabled
        ? t("relatieDetailView.donateur.nieuwsbrief.aan")
        : t("relatieDetailView.donateur.nieuwsbrief.uit");
  // Bedankmomenten column (Jmix CommunicatieBedankTileFragment parity). Jaarbedankje is fully
  // client-computed ("te versturen vanaf 1 jan next year") and Persoonlijke kaart is a static stub —
  // exactly like Jmix, which has no datamodel behind either; Bedankmail derives from stats.
  const jaarbedankjeVanaf = `${new Date().getFullYear() + 1}-01-01`;
  return (
    <Tile title={kop}>
      <div className="fawkes-relatie-tile">
        <div className="fawkes-donateur-kolommen">
          <div className="fawkes-donateur-kolom">
            <div className="fawkes-donateur-kolom__kop">
              <Mail size={16} aria-hidden="true" className="fawkes-donateur-kolom__icoon" />
              <span className="fawkes-donateur-kolom__titel">{t("relatieDetailView.donateur.communicatieKolom")}</span>
            </div>
            <div className="fawkes-donateur-regels">
              <div className="fawkes-donateur-regel">
                <CalendarDays size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.laatsteContact")}</span>
                <span className={waardeKlasse(communicatie.lastContactAt)}>
                  {datum(communicatie.lastContactAt, locale, t)}
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <Mail size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.laatsteBedankmail")}</span>
                <span className={waardeKlasse(stats.laatsteBedankmail)}>
                  {datum(stats.laatsteBedankmail, locale, t)}
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <Newspaper size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.nieuwsbrief.label")}</span>
                <span className="fawkes-donateur-regel__waarde">
                  <StatusBadge tone={communicatie.newsletterEnabled ? "success" : "neutral"}>{newsletter}</StatusBadge>
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <User size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.voorkeur")}</span>
                <span className={waardeKlasse(communicatie.preferredChannel)}>
                  {communicatie.preferredChannel || t("relatieDetailView.donateur.onbekend")}
                </span>
              </div>
            </div>
          </div>

          <div className="fawkes-donateur-kolom fawkes-donateur-kolom--bedank">
            <div className="fawkes-donateur-kolom__kop">
              <Heart size={16} aria-hidden="true" className="fawkes-donateur-kolom__icoon" />
              <span className="fawkes-donateur-kolom__titel">{t("relatieDetailView.donateur.bedankmomenten")}</span>
            </div>
            <div className="fawkes-donateur-regels">
              <div className="fawkes-donateur-regel">
                <Euro size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.laatsteDonatie")}</span>
                <span className={waardeKlasse(stats.laatsteDonatie)}>
                  {datum(stats.laatsteDonatie, locale, t)}
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <Mail size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.bedankmail")}</span>
                <span className="fawkes-donateur-regel__waarde">
                  <StatusBadge tone={stats.laatsteBedankmail ? "success" : "warning"}>
                    {stats.laatsteBedankmail
                      ? t("relatieDetailView.donateur.bedank.verzondenOp", { args: [formatDate(stats.laatsteBedankmail, locale)] })
                      : t("relatieDetailView.donateur.bedank.nogTeVersturen")}
                  </StatusBadge>
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <CalendarDays size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.jaarbedankje")}</span>
                <span className="fawkes-donateur-regel__waarde">
                  <StatusBadge tone="warning">
                    {t("relatieDetailView.donateur.bedank.teVersturenVanaf", { args: [formatDate(jaarbedankjeVanaf, locale)] })}
                  </StatusBadge>
                </span>
              </div>
              <div className="fawkes-donateur-regel">
                <MailOpen size={17} aria-hidden="true" className="fawkes-donateur-regel__icoon" />
                <span className="fawkes-donateur-regel__label">{t("relatieDetailView.donateur.persoonlijkeKaart")}</span>
                <span className="fawkes-donateur-regel__waarde">
                  <StatusBadge tone="neutral">{t("relatieDetailView.donateur.bedank.nietGepland")}</StatusBadge>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="fawkes-donateur-notitie">
          <span className="fawkes-donateur-notitie__titel">{t("relatieDetailView.donateur.interneNotitie")}</span>
          <span
            className={`fawkes-donateur-notitie__tekst${communicatie.internalNote ? "" : " fawkes-donateur-notitie__tekst--leeg"}`}
          >
            {communicatie.internalNote || t("relatieDetailView.donateur.geenNotitie")}
          </span>
        </div>

        {/* De drie acties uit de bron. Daar hingen ze aan een lege klikafhandelaar; hier staan ze uit
            met de reden erbij, zodat je niet op een knop drukt die niets doet. */}
        <div className="fawkes-donateur-voet">
          <button type="button" className="fawkes-donateur-voet__actie" disabled title={t("relatieDetailView.donateur.actieLater")}>
            <Mail size={14} aria-hidden="true" /> {t("relatieDetailView.donateur.bedankmailSturen")}
          </button>
          <button type="button" className="fawkes-donateur-voet__actie" disabled title={t("relatieDetailView.donateur.actieLater")}>
            <Send size={14} aria-hidden="true" /> {t("relatieDetailView.donateur.berichtVersturen")}
          </button>
          <button type="button" className="fawkes-donateur-voet__actie" disabled title={t("relatieDetailView.donateur.actieLater")}>
            <CalendarDays size={14} aria-hidden="true" /> {t("relatieDetailView.donateur.kaartjePlannen")}
          </button>
          <Button variant="secondary" className="fawkes-donateur-voet__bewerk" onClick={() => { setNotice(null); setEditing(true); }}>
            {t("relatieDetailView.donateur.bewerken")}
          </Button>
        </div>
        {nt && <p className={notice === "saved" ? "fawkes-donateur-notice" : "fawkes-error"} role="status">{nt}</p>}
      </div>
    </Tile>
  );
}
